import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-card/80 border border-border/70 text-foreground shadow-sm backdrop-blur-sm hover:bg-card hover:border-foreground/25 hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-lg hover:shadow-destructive/20 hover:-translate-y-0.5",
        outline:
          "border-2 border-border/60 bg-background/50 hover:bg-card hover:border-primary/40 hover:shadow-md hover:shadow-primary/10 backdrop-blur-sm hover:-translate-y-0.5",
        secondary:
          "bg-gradient-to-r from-secondary to-secondary-glow text-secondary-foreground shadow-md hover:shadow-xl hover:shadow-secondary/25 hover:-translate-y-0.5",
        ghost: "hover:bg-accent/10 hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline hover:text-accent",
        premium: "animate-premium-gradient-hover text-accent-foreground font-bold shadow-premium hover:-translate-y-1 hover:shadow-[0_0_40px_hsla(25,95%,58%,0.7)]",
        cta: "animate-premium-gradient text-accent-foreground font-bold shadow-premium hover:-translate-y-1.5 hover:shadow-[0_0_50px_hsla(25,95%,58%,0.8)] active:scale-[0.97]",
        vibrant: "bg-gradient-to-r from-[hsl(158_84%_36%)] via-[hsl(168_76%_40%)] to-[hsl(140_68%_44%)] text-white font-bold shadow-lg bg-[length:200%_100%] animate-smooth-gradient hover:-translate-y-1 hover:shadow-[0_0_40px_hsla(158,80%,42%,0.5)]",
        hero: "bg-gradient-to-r from-[hsl(350_72%_45%)] via-[hsl(20_90%_52%)] to-[hsl(45_100%_60%)] text-white font-bold text-base shadow-xl bg-[length:200%_100%] animate-smooth-gradient hover:-translate-y-1.5 hover:shadow-[0_0_50px_hsla(35,95%,55%,0.55)] active:scale-[0.98]",
        /* Location-specific palettes — every colored button is chosen for where it lives */
        foyer: "bg-gradient-to-r from-[hsl(345_70%_42%)] to-[hsl(330_68%_50%)] text-white font-semibold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(340,70%,48%,0.5)] hover:-translate-y-0.5 active:scale-[0.98]",
        boxoffice: "bg-gradient-to-r from-[hsl(42_96%_58%)] to-[hsl(28_92%_52%)] text-[hsl(30_40%_12%)] font-bold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(35,95%,55%,0.55)] hover:-translate-y-0.5 active:scale-[0.98]",
        vault: "bg-gradient-to-r from-[hsl(35_60%_46%)] to-[hsl(20_55%_34%)] text-[hsl(40_60%_94%)] font-semibold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(28,60%,40%,0.55)] hover:-translate-y-0.5 active:scale-[0.98]",
        archive: "bg-gradient-to-r from-[hsl(348_58%_34%)] to-[hsl(0_52%_20%)] text-white font-semibold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(350,60%,30%,0.6)] hover:-translate-y-0.5 active:scale-[0.98]",
        lobby: "bg-gradient-to-r from-[hsl(348_74%_52%)] to-[hsl(28_92%_58%)] text-white font-semibold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(10,80%,52%,0.5)] hover:-translate-y-0.5 active:scale-[0.98]",
        ink: "bg-gradient-to-r from-[hsl(268_62%_46%)] to-[hsl(312_64%_48%)] text-white font-semibold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(290,62%,48%,0.5)] hover:-translate-y-0.5 active:scale-[0.98]",
        night: "bg-gradient-to-r from-[hsl(322_74%_52%)] to-[hsl(45_98%_58%)] text-[hsl(320_40%_12%)] font-bold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(340,80%,55%,0.5)] hover:-translate-y-0.5 active:scale-[0.98]",
        screen: "bg-gradient-to-r from-[hsl(196_70%_46%)] to-[hsl(220_62%_44%)] text-white font-semibold shadow-md hover:shadow-[0_10px_30px_-10px_hsla(205,68%,46%,0.5)] hover:-translate-y-0.5 active:scale-[0.98]",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-12 rounded-xl px-8 text-base",
        xl: "h-14 rounded-xl px-10 text-lg font-bold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
